import Typography from '@mui/material/Typography';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { BaseDialog, TDialogAction } from '@mgm/common/components/base';
import { useAddMembersAPI } from '../../../hooks/api';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getInviteMembers } from '../../../store/onboarding/selector';
import { useOptions } from '../../../hooks/useOptions';
import { TAddMembersReq } from '../../../types/requests';
import { onboardingActions } from '../../../store/onboarding/slice';
import { ROUTES } from '../../../data';

type TAddMembersDialog = {
  setIsOpenDialog: (val: boolean) => void
  isOpenDialog: boolean
  isOnboarding?: boolean
};

const AddMembersDialog: FC<TAddMembersDialog> = ({ setIsOpenDialog, isOpenDialog, isOnboarding = true }) => {
  const { t } = useTranslation(['common', 'team']);
  const teamMembers = useAppSelector(getInviteMembers);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { roleMap } = useOptions();

  const {
    mutateAsync: addMembersMutate,
    isLoading: isAddMembersLoading,
    isSuccess,
  } = useAddMembersAPI();

  const dispatch = useAppDispatch();

  const getValidMembers = useMemo(() => teamMembers.filter((row) => row.email !== ''), [teamMembers]);

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar(`${t('membersWereAdded', { ns: 'team' })}`, {
        variant: 'success',
      });
      navigate(ROUTES.TEAM);
    }
  }, [isSuccess]);

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
  };

  const saveMembers = async () => {
    const rowsWithData = teamMembers.filter((row) => row.email !== '');

    const newMember = rowsWithData.map((row) => ({
      email: row.email,
      name: isOnboarding ? `${row.firstName} ${row.lastName}` : row.name,
      locations: row?.locations || [],
      permissionLevel: roleMap.toKey(row.permissionLevel as 0 | 1 | 2),
      isRegistered: true,
    })) as TAddMembersReq['membersData'];

    await addMembersMutate({ newMember });
    dispatch(onboardingActions.setInviteMembers([]));
  };

  const dialogActions: Array<TDialogAction> = [
    {
      label: t('cancel', { ns: 'common' }),
      variant: 'outlined',
      color: 'primary',
      isDisabled: isAddMembersLoading,
      onClick: () => handleCloseDialog(),
    },
    {
      label: t('inviteMembers', { ns: 'team' }),
      color: 'primary',
      isDisabled: !getValidMembers.length,
      isLoading: isAddMembersLoading,
      onClick: () => saveMembers(),
    },
  ];

  return (
    <BaseDialog
      dialogWidth={400}
      dialogContent={(
        <Typography variant="body1" pb={2}>
          {t('youAreAbout', { ns: 'team' })}
          {' '}
          {getValidMembers?.length}
          {' '}
          {t('membersRecords', { ns: 'team' })}
          {' '}
          {t('continue', { ns: 'team' })}
        </Typography>
      )}
      isOpenDialog={isOpenDialog}
      dialogTitle={t('confirmAction')}
      handleClose={handleCloseDialog}
      dialogActions={dialogActions}
    />
  );
};

export {
  AddMembersDialog,
};
